import { Region } from '@seatsio/seatsio-react';

/**
 * Single source of truth for the seats.io widget theming we apply across the green zone.
 *
 * seats.io's widget API requires concrete colour strings (it cannot resolve CSS custom
 * properties), so the brand hex values are duplicated here. Update both this file AND the
 * matching CSS tokens in `src/styles/punchup-tokens.css` when the
 * brand colours change.
 *
 * The key set is intentionally minimal — see `SeatsSection/CLAUDE.md` § "Theming the
 * embedded widget" for the rationale on which `colors` keys we deliberately do NOT set.
 */

/** Mirrors `--punchup-color-primary` from `src/styles/punchup-tokens.css`. */
export const PUNCHUP_PRIMARY = '#FF5E2A';

/** Mirrors `--punchup-color-danger` from `src/styles/punchup-tokens.css`. */
export const PUNCHUP_DANGER = '#ef4444';

/**
 * Branded seats.io colour overrides. Passed to every `SeatsioEventManager` render in the
 * green zone via `<PunchupSeatsWidget>`.
 */
export const SEATS_COLORS = {
	selectedObjectColor: PUNCHUP_PRIMARY,
	colorSelected: PUNCHUP_PRIMARY,
	errorColor: PUNCHUP_DANGER,
} as const;

/**
 * Branded seats.io style overrides. `font` is one of a strict enum; `Inter` is the only
 * value that matches `--punchup-font-family`. `cornerRadius` and `buttonShape` accept only
 * `'round' | 'square'` — there is no continuous radius value.
 */
export const SEATS_STYLE = {
	font: 'Inter',
	cornerRadius: 'round',
	buttonShape: 'round',
} as const;

/**
 * Languages the seats.io v15.x widget accepts. `i18n.language` is a free string that may
 * carry a region tag (`'fr-FR'`); we strip the region and fall back to `'en'` for
 * unsupported codes.
 */
export const SEATS_LANGUAGES = ['de', 'en', 'es', 'fr', 'nl', 'pt'] as const;
export type SeatsLanguage = typeof SEATS_LANGUAGES[number];

export const toSeatsLanguage = (raw: string | undefined): SeatsLanguage => {
	const code = (raw ?? '').split('-')[0];
	return (SEATS_LANGUAGES as readonly string[]).includes(code) ? (code as SeatsLanguage) : 'en';
};

/**
 * seats.io hosting regions. `partnerData.seats_region` is a free backend string; we validate
 * it against the SDK's closed `Region` union and fall back to `'na'` for anything unrecognised
 * so malformed data never reaches the SDK.
 */
export const SEATS_REGIONS = ['eu', 'na', 'sa', 'oc'] as const;

export const toSeatsRegion = (raw: string | undefined): Region =>
	(SEATS_REGIONS as readonly string[]).includes(raw ?? '') ? (raw as Region) : 'na';
